import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, X, Star, Shield, Crown, Building, ArrowRight } from 'lucide-react';
import { membershipApi, billingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const MembershipPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [interval, setInterval] = useState('yearly');

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await membershipApi.getPlans();
        setPlans(data);
      } catch {
        console.error('Error fetching plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/membership' } } });
      return;
    }
    if (planId === 'free') return;

    setUpgrading(planId);
    try {
      const { data } = await billingApi.createCheckout(planId, interval);
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setUpgrading(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (typeof msg === 'string' && msg.length < 200 && !msg.toLowerCase().includes('emergent')) {
        toast.error(msg);
      } else {
        toast.error('Payment is temporarily unavailable. Please contact sales@eagles-way.com');
      }
      setUpgrading(null);
    }
  };

  const getIcon = (planId) => {
    switch (planId) {
      case 'silver': return <Star className="w-6 h-6 text-slate-400" />;
      case 'gold': return <Shield className="w-6 h-6 text-amber-500" />;
      case 'vip': return <Crown className="w-6 h-6 text-purple-500" />;
      default: return <Building className="w-6 h-6 text-slate-400" />;
    }
  };

  const getPrice = (plan) => {
    if (plan.plan_id === 'free') return { amount: 0, period: '' };
    const monthly = plan.monthly_price || 0;
    const yearly = plan.yearly_price || 0;
    if (interval === 'monthly') return { amount: monthly, period: '/mo' };
    return { amount: yearly, period: '/yr' };
  };

  const comparisonFeatures = [
    { feature: 'Products Listing', free: '5', silver: '50', gold: 'Unlimited', vip: 'Unlimited' },
    { feature: 'Monthly Inquiries', free: '5', silver: '50', gold: 'Unlimited', vip: 'Unlimited' },
    { feature: 'View Buyer Contacts', free: false, silver: true, gold: true, vip: true },
    { feature: 'Send Quotes on RFQs', free: false, silver: true, gold: true, vip: true },
    { feature: 'RFQ Access', free: 'Basic', silver: 'Priority', gold: 'Full', vip: 'Full + Alerts' },
    { feature: 'Featured Products', free: '0', silver: '5', gold: '20', vip: '50' },
    { feature: 'Verified Badge', free: false, silver: false, gold: true, vip: true },
    { feature: 'Analytics Dashboard', free: false, silver: true, gold: true, vip: true },
    { feature: 'Priority Support', free: false, silver: false, gold: true, vip: true },
    { feature: 'Dedicated Account Manager', free: false, silver: false, gold: false, vip: true },
    { feature: 'Homepage Featured', free: false, silver: false, gold: false, vip: true },
    { feature: 'Higher Search Ranking', free: false, silver: false, gold: true, vip: true },
    { feature: 'Custom Branding', free: false, silver: false, gold: false, vip: true },
    { feature: 'Free Trial', free: '-', silver: '7 days', gold: '-', vip: '-' },
  ];

  const renderCell = (val) => {
    if (val === true) return <Check className="w-4 h-4 text-green-500 mx-auto" />;
    if (val === false) return <X className="w-4 h-4 text-slate-300 mx-auto" />;
    return <span>{val}</span>;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Hero */}
      <section className="bg-[#0A192F] py-16">
        <div className="container-main text-center">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#EA580C] mb-4">Premium Membership</p>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            Grow Your Business with the Right Plan
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Unlock buyer contacts, send unlimited quotes, get verified badges, and dominate search results.
          </p>

          {/* Monthly / Yearly Toggle */}
          <div className="inline-flex items-center bg-[#0F2440] rounded-full p-1 border border-slate-600" data-testid="billing-interval-toggle">
            <button
              onClick={() => setInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                interval === 'monthly' ? 'bg-[#EA580C] text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                interval === 'yearly' ? 'bg-[#EA580C] text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="toggle-yearly"
            >
              Yearly
              <span className="ml-1.5 text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      <div className="container-main py-16 -mt-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/2 mb-4" />
                <div className="h-8 bg-slate-200 rounded w-3/4 mb-4" />
                <div className="space-y-2">
                  {[...Array(5)].map((_, j) => <div key={j} className="h-4 bg-slate-200 rounded" />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const isCurrentPlan = user?.membership === plan.plan_id;
              const isGold = plan.plan_id === 'gold';
              const isVip = plan.plan_id === 'vip';
              const price = getPrice(plan);

              return (
                <div
                  key={plan.plan_id}
                  className={`bg-white overflow-hidden transition-all ${
                    isGold
                      ? 'border-2 border-[#EA580C] shadow-lg ring-1 ring-[#EA580C]/20'
                      : 'border border-slate-200 hover:shadow-md'
                  }`}
                  data-testid={`membership-plan-${plan.plan_id}`}
                >
                  {isGold && (
                    <div className="bg-[#EA580C] py-1.5 text-center text-xs font-bold text-white tracking-wider uppercase">
                      Most Popular
                    </div>
                  )}
                  {isVip && (
                    <div className="bg-[#0A192F] py-1.5 text-center text-xs font-bold text-white tracking-wider uppercase">
                      Best Value
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-center gap-2.5 mb-5">
                      {getIcon(plan.plan_id)}
                      <h3 className="font-bold text-lg text-[#0A192F]">{plan.name}</h3>
                    </div>

                    <div className="mb-1">
                      <span className="text-4xl font-bold text-[#0A192F] tracking-tight">
                        ${price.amount > 0 ? (Number.isInteger(price.amount) ? price.amount : price.amount.toFixed(2)) : '0'}
                      </span>
                      {price.period && <span className="text-slate-400 text-sm">{price.period}</span>}
                    </div>
                    {plan.trial_days > 0 && (
                      <p className="text-xs text-[#EA580C] font-semibold mb-5">{plan.trial_days}-day free trial</p>
                    )}
                    {plan.trial_days === 0 && plan.plan_id !== 'free' && (
                      <p className="text-xs text-transparent mb-5 select-none">-</p>
                    )}
                    {plan.plan_id === 'free' && <p className="text-xs text-transparent mb-5 select-none">-</p>}

                    <ul className="space-y-2.5 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                      {plan.restricted?.map((item, i) => (
                        <li key={`r-${i}`} className="flex items-start gap-2 text-sm">
                          <X className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                          <span className="text-slate-400">{item}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrentPlan ? (
                      <Button className="w-full rounded-sm" variant="outline" disabled data-testid={`plan-current-${plan.plan_id}`}>
                        <Check className="w-4 h-4 mr-1.5" /> Current Plan
                      </Button>
                    ) : plan.price === 0 && plan.plan_id === 'free' ? (
                      <Button className="w-full rounded-sm" variant="outline" disabled>Free Plan</Button>
                    ) : (
                      <Button
                        className={`w-full rounded-sm font-semibold ${
                          isGold ? 'bg-[#EA580C] hover:bg-[#C2410C]' : 'bg-[#0A192F] hover:bg-[#172A46]'
                        }`}
                        onClick={() => handleUpgrade(plan.plan_id)}
                        disabled={upgrading === plan.plan_id}
                        data-testid={`membership-upgrade-${plan.plan_id}`}
                      >
                        {upgrading === plan.plan_id ? 'Processing...' : (
                          <>
                            {plan.trial_days > 0 ? `Start ${plan.trial_days}-Day Free Trial` : 'Subscribe Now'}
                            <ArrowRight className="w-4 h-4 ml-1.5" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Comparison Table */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-center text-[#0A192F] mb-2 tracking-tight">Compare All Features</h2>
          <p className="text-center text-slate-500 mb-8 text-sm">See exactly what you get with each plan</p>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="membership-comparison-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-800">Feature</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-800">Free</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-800">Silver</th>
                  <th className="text-center p-4 text-sm font-semibold text-[#EA580C]">Gold</th>
                  <th className="text-center p-4 text-sm font-semibold text-purple-700">VIP</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50">
                    <td className="p-4 text-sm text-slate-700">{row.feature}</td>
                    <td className="p-4 text-sm text-center text-slate-500">{renderCell(row.free)}</td>
                    <td className="p-4 text-sm text-center text-slate-600">{renderCell(row.silver)}</td>
                    <td className="p-4 text-sm text-center text-slate-700 font-medium">{renderCell(row.gold)}</td>
                    <td className="p-4 text-sm text-center text-slate-700 font-medium">{renderCell(row.vip)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Contact Sales CTA */}
        <div className="mt-16 text-center bg-white border border-slate-200 p-8 max-w-2xl mx-auto">
          <h3 className="font-bold text-lg text-[#0A192F] mb-2">Need a Custom Plan?</h3>
          <p className="text-slate-500 text-sm mb-4">
            For enterprise pricing, bulk licensing, or custom requirements — our sales team is ready to help.
          </p>
          <a
            href="mailto:sales@eagles-way.com?subject=Enterprise%20Membership%20Inquiry"
            className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#172A46] text-white font-semibold px-6 py-2.5 rounded-sm transition-colors text-sm"
            data-testid="contact-sales-btn"
          >
            Contact Sales
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-[#0A192F] mb-8 tracking-tight">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I upgrade or downgrade anytime?', a: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the end of your billing cycle.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit/debit cards through Stripe. Enterprise invoicing is available for VIP plans.' },
              { q: 'How does the free trial work?', a: 'Silver plan gets a 7-day free trial with full access. Cancel anytime before the trial ends. No charge until the trial expires.' },
              { q: 'What happens when my membership expires?', a: 'Your account will revert to the Free plan. Your products and data are preserved, but premium features will be restricted.' },
              { q: 'Can I switch between monthly and yearly billing?', a: 'Yes. Yearly billing saves you about 17% compared to monthly. You can switch at any time from your billing dashboard.' },
            ].map((faq, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5">
                <h4 className="font-semibold text-[#0A192F] text-sm mb-2">{faq.q}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MembershipPage;
